import { useState, useCallback, useEffect, useRef, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '@/hooks/useLocalization';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { analyticsService } from '@/services/analytics/analyticsService';
import { showToast } from '@/utils/globalToast';
import { ROUTES } from '@/app/router/routes';
import type { SignUpFormData, SignUpState } from '../hooks/useSignup';
import {
  validateName,
  validateMobile,
  isValidEmail,
  validatePasswords,
  CREATE_PASSWORD_MESSAGE,
  PASSWORD_TOOLTIP_SPECIAL_CHARACTERS,
} from '@/utils/formValidators';
import { DEFAULT_PHONE_CODE } from '@/config/countryCodes';
import { AuthLogo } from './AuthLogo';
import { MobileAuthHeader } from './MobileAuthHeader';
import {
  AuthInfoTooltip,
  AuthTextField,
  PasswordToggle,
  authControlStyles as controls,
} from './AuthControls';
import { VerifyAccountCard } from './VerifyAccountCard';
import { PrivacyConsent } from './PrivacyConsent';
import { CountryCodeSelect } from './CountryCodeSelect';
import { AccountVerifiedDialog } from './AccountVerifiedDialog';
import { SupportWarningDialog } from './SupportWarningDialog';
import styles from './CreateAccountForm.module.css';

/**
 * CreateAccountForm — email sign up, the form column of /signUp.
 *
 * Mirrors Flutter WebSignUpFormNew (presentation/web/auth/web_signup/web_signup_page.dart) +
 * WebSignupBloc (email sign up). Once the account is created, the column switches to the
 * verify-email card (Flutter VerifyAccCard). A failed sign up shows Flutter's message as a toast
 * (CustomFlushBar); a rejected password shows the support dialog.
 *
 * Form fields, in Flutter's order, and validators (FormValidators):
 *   - First Name, Last Name (required, letters of any script plus ' - . — validateName; Flutter
 *     drops everything outside [a-zA-Z\s] while typing, which cut "José" to "Jos")
 *   - Mobile No with the country code prefix (validateMobile with the per-country digit counts;
 *     duplicate check)
 *   - Email ID (isValidEmail; duplicate check)
 *   - Create Password (with the password rules tooltip), Re-type your password (validatePasswords)
 *   - Privacy/T&C consent checkbox (required)
 * and "Already have an account ? Login" (→ /login with the same return destination).
 *
 * Flutter validates on Continue only. Here a field is also checked when it loses focus once it
 * has been edited, and names are trimmed then; Continue checks everything and moves focus to the
 * first field with a message. While the consent box is unticked, a note under Continue says why
 * it is disabled.
 */

type FieldName = 'firstName' | 'lastName' | 'email' | 'phone' | 'password' | 'confirmPassword';

/** FieldError ids, also used by aria-describedby */
const ERROR_ID: Record<FieldName, string> = {
  firstName: 'sign-up-first-name-error',
  lastName: 'sign-up-last-name-error',
  email: 'sign-up-email-error',
  phone: 'sign-up-mobile-error',
  password: 'sign-up-password-error',
  confirmPassword: 'sign-up-confirm-password-error',
};

/** Input ids, in form order (focus goes to the first one with a message) */
const INPUT_ID: Record<FieldName, string> = {
  firstName: 'sign-up-first-name',
  lastName: 'sign-up-last-name',
  email: 'sign-up-email',
  phone: 'sign-up-mobile',
  password: 'sign-up-password',
  confirmPassword: 'sign-up-confirm-password',
};
const FIELD_ORDER: FieldName[] = ['firstName', 'lastName', 'phone', 'email', 'password', 'confirmPassword'];

const CONSENT_HINT_ID = 'sign-up-consent-hint';
const CONSENT_HINT = 'Please agree to the terms and privacy policy above to continue';

interface CreateAccountFormProps {
  signup: SignUpState;
}

export function CreateAccountForm({ signup }: CreateAccountFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const {
    phase,
    isLoading,
    error,
    isEmailDuplicate,
    isMobileDuplicate,
    registeredEmail,
    disableResendEmail,
    warningWithSupport,
    warningMessage,
    accountVerifiedDialog,
    handleSignUp,
    handleResendVerification,
    handleReSignUp,
    handleLogout,
    handleAccountVerifiedDialogClose,
    clearError,
    clearDuplicate,
    onResendTimerEnd,
  } = signup;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(DEFAULT_PHONE_CODE);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);

  // Validation errors
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  /** Fields edited since the page opened — only those are checked on blur */
  const dirty = useRef<Partial<Record<FieldName, boolean>>>({});
  const markDirty = (field: FieldName) => { dirty.current[field] = true; };

  // Password-policy warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  useEffect(() => {
    if (warningWithSupport && warningMessage) setShowWarningDialog(true);
  }, [warningWithSupport, warningMessage]);

  // Flutter CustomFlushBar.failureOrSuccessFlushBar(message: state.showMessage)
  useEffect(() => {
    if (!error || warningWithSupport) return;
    showToast(error, 'error');
    clearError();
  }, [error, warningWithSupport, clearError]);

  // ── Validation (mirrors Flutter FormValidators) ────────────────

  /** The message for one field, from the given values (the duplicate flags win, as in Flutter). */
  const fieldMessage = useCallback((field: FieldName, values: {
    firstName: string; lastName: string; phone: string; email: string; password: string; confirmPassword: string;
  }): string => {
    switch (field) {
      case 'firstName':
        return validateName(values.firstName, t('First Name'), t);
      case 'lastName':
        return validateName(values.lastName, t('Last Name'), t);
      case 'phone':
        // The duplicate messages are Flutter's validator results while the duplicate flag is set
        return isMobileDuplicate
          ? t('Mobile number already exists. Please use a different mobile number.')
          : validateMobile(values.phone, countryCode, t);
      case 'email':
        return isEmailDuplicate
          ? t('Email already exists. Please use a different email address.')
          : isValidEmail(values.email.trim(), t);
      case 'password':
        return validatePasswords({ pass: values.password }, t);
      case 'confirmPassword':
        return validatePasswords({ pass: values.password, confirmPass: values.confirmPassword, isConfirm: true }, t);
    }
  }, [countryCode, isEmailDuplicate, isMobileDuplicate, t]);

  const setFieldError: Record<FieldName, (message: string) => void> = {
    firstName: setFirstNameError,
    lastName: setLastNameError,
    email: setEmailError,
    phone: setPhoneError,
    password: setPasswordError,
    confirmPassword: setConfirmPasswordError,
  };

  const currentValues = { firstName, lastName, phone, email, password, confirmPassword };

  /** Blur: an edited field shows its message (names are trimmed first, as on Continue). */
  const handleBlur = (field: FieldName) => {
    const values = { ...currentValues };
    if (field === 'firstName' || field === 'lastName') {
      const trimmed = values[field].trim();
      if (trimmed !== values[field]) (field === 'firstName' ? setFirstName : setLastName)(trimmed);
      values[field] = trimmed;
    }
    if (!dirty.current[field]) return;
    setFieldError[field](fieldMessage(field, values));
    // The re-typed password depends on the first one
    if (field === 'password' && dirty.current.confirmPassword) {
      setConfirmPasswordError(fieldMessage('confirmPassword', values));
    }
  };

  /** Continue: every field; returns the first field with a message. */
  const validate = useCallback((): FieldName | null => {
    const values = { firstName, lastName, phone, email, password, confirmPassword };
    let firstInvalid: FieldName | null = null;
    for (const field of FIELD_ORDER) {
      const message = fieldMessage(field, values);
      setFieldError[field](message);
      if (message && !firstInvalid) firstInvalid = field;
    }
    return firstInvalid;
    // setFieldError holds stable state setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, phone, email, password, confirmPassword, fieldMessage]);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    clearError();
    // Names are saved trimmed (the bloc trims them too)
    if (firstName !== firstName.trim()) setFirstName(firstName.trim());
    if (lastName !== lastName.trim()) setLastName(lastName.trim());
    const firstInvalid = validate();
    if (firstInvalid) {
      document.getElementById(INPUT_ID[firstInvalid])?.focus();
      return;
    }
    if (!agreeToPolicy) return;

    const formData: SignUpFormData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      countryCode,
      email: email.trim().toLowerCase(),
      password,
      confirmPassword,
      referralCode: '', // referral code validation is commented out in Flutter email signup
      agreeToPolicy,
    };

    handleSignUp(formData);
  }, [
    validate, agreeToPolicy, firstName, lastName, phone, countryCode, email,
    password, confirmPassword, handleSignUp, clearError,
  ]);

  // navigateTo(CoreRoute.loginRoute, arguments: state.profileInput)
  function openLogin() {
    void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
    navigate(ROUTES.LOGIN, { state: location.state });
  }

  // ── Verify-email state ────────────────────────────────────────

  if (phase === 'verifyEmail') {
    return (
      <div className={styles.verifyWrapper}>
        <VerifyAccountCard
          email={registeredEmail}
          disableResendBtn={disableResendEmail}
          onResend={handleResendVerification}
          onReSignUp={handleReSignUp}
          onLogout={handleLogout}
          onTimerEnd={onResendTimerEnd}
          isLoading={isLoading}
        />
        {accountVerifiedDialog && (
          <AccountVerifiedDialog
            showContinueApply={accountVerifiedDialog.showContinueApply}
            onClose={handleAccountVerifiedDialogClose}
          />
        )}
      </div>
    );
  }

  // ── Sign up form ──────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      {isMobile ? <MobileAuthHeader /> : <div className={styles.topGap} aria-hidden />}
      <AuthLogo className={styles.logo} />

      <section className={styles.content} aria-labelledby="create-account-title">
        <h1 id="create-account-title" className={styles.title}>{t('Create your account')}</h1>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <AuthTextField
            id={INPUT_ID.firstName}
            label={t('First Name')}
            required
            type="text"
            autoFocus
            placeholder={t('Enter your first name')}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              markDirty('firstName');
              if (firstNameError) setFirstNameError('');
            }}
            onBlur={() => handleBlur('firstName')}
            autoComplete="given-name"
            error={firstNameError}
            errorId={ERROR_ID.firstName}
            disabled={isLoading}
          />

          <AuthTextField
            id={INPUT_ID.lastName}
            label={t('Last Name')}
            required
            type="text"
            placeholder={t('Enter your last name')}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              markDirty('lastName');
              if (lastNameError) setLastNameError('');
            }}
            onBlur={() => handleBlur('lastName')}
            autoComplete="family-name"
            error={lastNameError}
            errorId={ERROR_ID.lastName}
            disabled={isLoading}
          />

          {/* Flutter errorText while the duplicate flag is set: ErrorConstants.phoneNoExist */}
          <AuthTextField
            id={INPUT_ID.phone}
            label={t('Mobile No')}
            required
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value.replace(/[^0-9\s-]/g, ''));
              markDirty('phone');
              if (phoneError) setPhoneError('');
              if (isMobileDuplicate) clearDuplicate('mobile');
            }}
            onBlur={() => handleBlur('phone')}
            autoComplete="tel-national"
            startAdornment={
              <CountryCodeSelect
                value={countryCode}
                onChange={(code) => {
                  setCountryCode(code);
                  if (phoneError) setPhoneError('');
                }}
                disabled={isLoading}
              />
            }
            error={phoneError || (isMobileDuplicate ? t('Phone no already exists!') : '')}
            errorId={ERROR_ID.phone}
            disabled={isLoading}
          />

          {/* Flutter errorText while the duplicate flag is set: ErrorConstants.emailExist */}
          <AuthTextField
            id={INPUT_ID.email}
            label={t('Email ID')}
            required
            type="email"
            placeholder={t('Enter your Email Id')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              markDirty('email');
              if (emailError) setEmailError('');
              if (isEmailDuplicate) clearDuplicate('email');
            }}
            onBlur={() => handleBlur('email')}
            autoComplete="email"
            error={emailError || (isEmailDuplicate ? t('Email already exists!') : '')}
            errorId={ERROR_ID.email}
            disabled={isLoading}
          />

          <AuthTextField
            id={INPUT_ID.password}
            label={t('Create Password')}
            required
            labelAddon={<AuthInfoTooltip message={t(CREATE_PASSWORD_MESSAGE) + PASSWORD_TOOLTIP_SPECIAL_CHARACTERS} />}
            type={showPassword ? 'text' : 'password'}
            placeholder={t('Enter your Password')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              markDirty('password');
              if (passwordError) setPasswordError('');
            }}
            onBlur={() => handleBlur('password')}
            autoComplete="new-password"
            endAdornment={<PasswordToggle shown={showPassword} onToggle={() => setShowPassword(!showPassword)} />}
            error={passwordError}
            errorId={ERROR_ID.password}
            disabled={isLoading}
          />

          <AuthTextField
            id={INPUT_ID.confirmPassword}
            label={t('Re-type your password')}
            required
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder={t('Re-type your password')}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              markDirty('confirmPassword');
              if (confirmPasswordError) setConfirmPasswordError('');
            }}
            onBlur={() => handleBlur('confirmPassword')}
            autoComplete="new-password"
            endAdornment={
              <PasswordToggle shown={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
            }
            error={confirmPasswordError}
            errorId={ERROR_ID.confirmPassword}
            disabled={isLoading}
          />

          <PrivacyConsent checked={agreeToPolicy} onChange={setAgreeToPolicy} />

          <button
            type="submit"
            className={`${controls.webPrimaryButton} ${styles.continueBtn}`}
            disabled={!agreeToPolicy || isLoading}
            aria-describedby={!agreeToPolicy ? CONSENT_HINT_ID : undefined}
          >
            {t('Continue')}
          </button>
          {/* Why Continue is disabled — visible text, so it works on touch screens too */}
          {!agreeToPolicy && (
            <p id={CONSENT_HINT_ID} className={styles.submitHint}>{t(CONSENT_HINT)}</p>
          )}
        </form>

        <p className={styles.switchRow}>
          <button type="button" className={styles.switchLink} onClick={openLogin}>
            {t('Already have an account ?')}
            <strong className={styles.switchAction}>{` ${t('Login')}`}</strong>
          </button>
        </p>
      </section>

      {showWarningDialog && (
        <SupportWarningDialog
          message={warningMessage}
          onOk={() => {
            setShowWarningDialog(false);
            clearError();
          }}
        />
      )}
    </div>
  );
}
